import { Alert, Box, Tooltip } from '@mui/material';
import { useWatch } from 'react-hook-form';

import { GroupedSelectFormField } from '@/components/Form/GroupedSelect';
import { TextInput } from '@/components/Form/TextInput';
import { Icon } from '@/components/Icon/Icon';
import { HintIconWrapper } from '@/components/PageForm/MoreInfoForm/drawers/QuestionsDrawer/QuestionDrawer.styled';

import { useTranslate } from '@/hooks/useTranslate';

import { UsTaxNoReturnReasonCode } from '@/types/api/tax-info';

import { getDataTestId } from '@/utils/browser';

import { UsTaxReturnIndiciaSectionProps } from '../TaxInfoDrawer.interface';
import { US_TAX_NO_RETURN_REASON_OTHER_MAX_LENGTH } from './constants';

import {
  TaxInfoDropdownRow,
  TaxInfoFieldLabel,
  TaxInfoSubSection,
} from '../TaxInfoDrawer.styled';

export function UsTaxReturnIndiciaSection({
  control,
  errors,
  isPending,
  noReturnReasonGroups,
  noReturnReasonOtherOption,
  resetField,
}: Readonly<UsTaxReturnIndiciaSectionProps>) {
  const { t } = useTranslate();

  const usTaxNoReturnReasonCode = useWatch({
    control,
    name: 'usTaxNoReturnReasonCode',
  });
  const usTaxNoReturnReasonOther = useWatch({
    control,
    name: 'usTaxNoReturnReasonOther',
  });
  const showOtherReasonField =
    usTaxNoReturnReasonCode === UsTaxNoReturnReasonCode.OTHER;
  const otherReasonCharactersLeft =
    US_TAX_NO_RETURN_REASON_OTHER_MAX_LENGTH -
    (usTaxNoReturnReasonOther?.length ?? 0);

  return (
    <TaxInfoSubSection>
      <Alert
        icon={<Icon name="alert-outline" />}
        color="warning"
        variant="standard"
        {...getDataTestId('tax-info-us-indicia-warning')}
      >
        {t('taxInfoDrawer.question1.usIndicia.warning')}
      </Alert>

      <TaxInfoFieldLabel variant="caption">
        {t('taxInfoDrawer.question1.noReturnReason.label')}
      </TaxInfoFieldLabel>

      <TaxInfoDropdownRow>
        <Box flex={1} minWidth={0}>
          <GroupedSelectFormField
            control={control}
            name="usTaxNoReturnReasonCode"
            selectionMode="single"
            title={t('taxInfoDrawer.question1.noReturnReason.placeholder')}
            placeholder={t(
              'taxInfoDrawer.question1.noReturnReason.placeholder',
            )}
            groups={noReturnReasonGroups}
            otherOption={noReturnReasonOtherOption}
            disabled={isPending}
            error={!!errors.usTaxNoReturnReasonCode}
            helperText={errors.usTaxNoReturnReasonCode?.message}
            onSelectChange={selectedValue => {
              if (selectedValue !== UsTaxNoReturnReasonCode.OTHER) {
                resetField('usTaxNoReturnReasonOther', { defaultValue: '' });
              }
            }}
          />
        </Box>
        <Tooltip
          title={t('taxInfoDrawer.question1.noReturnReason.tooltip')}
          placement="top"
          arrow
        >
          <HintIconWrapper>
            <Icon
              name="info-outline"
              color={theme => theme.palette.neutral[700]}
            />
          </HintIconWrapper>
        </Tooltip>
      </TaxInfoDropdownRow>

      {showOtherReasonField && (
        <TextInput
          name="usTaxNoReturnReasonOther"
          control={control}
          label={t('taxInfoDrawer.question1.noReturnReasonOther.label')}
          disabled={isPending}
          error={!!errors.usTaxNoReturnReasonOther}
          helperText={
            errors.usTaxNoReturnReasonOther?.message ??
            t('taxInfoDrawer.question1.noReturnReasonOther.charactersLeft', {
              count: otherReasonCharactersLeft,
            })
          }
          inputProps={{
            maxLength: US_TAX_NO_RETURN_REASON_OTHER_MAX_LENGTH,
          }}
          {...getDataTestId('tax-info-no-return-reason-other-input')}
        />
      )}
    </TaxInfoSubSection>
  );
}
